import {ResearchLitElement, html, css} from '@core/research-lit-element';

import '@bog-design-system/bd-font';
import '@bog-design-system/bd-standard-button';
import '@bog-design-system/bd-badge-overline';
import '@bog-design-system/bd-date';

export class ResearchCard extends ResearchLitElement {
    static get is() {
        return 'research-card';
    }

    static get styles() {
        // language=css
        return css`
            :host {
                display: flex;
                width: 100%;
                border: 1px solid var(--color-invert-component-tr-70);
                background-color: var(--color-invert-background-solid-40);
                border-radius: var(--border-radius-38);
                --badge-border-color: var(--color-invert-component-tr-80);
            }

            :host(:not([short-version])) .card-footer {
                margin-top: 0;
            }

            :host(:not([has-footer])) .card-container {
                padding-bottom: var(--space-32);
            }

            .short-card-container {
                display: flex;
                flex-direction: column;
                padding: var(--space-32) var(--space-48) 20px var(--space-48);
                width: 100%;
            }

            .short-card-content-container {
                display: grid;
                grid-template-columns: 864px 1fr;
                column-gap: var(--space-48);
            }

            .short-card-left-part {
                display: flex;
                flex-direction: column;
                row-gap: var(--space-24);
            }

            .short-card-right-part {
                display: flex;
                flex-direction: column;
                row-gap: var(--space-2);
                align-items: center;
            }

            .gift-badge {
                --background-color: var(--color-secondary-solid-60);
                --text-color: var(--color-secondary-solid-10);
                --badge-icon-color: var(--color-secondary-solid-10);
                box-shadow: unset;
            }

            .short-card-duration {
                color: var(--color-invert-component-tr-40);
            }

            .card-footer {
                display: flex;
                padding-top: var(--space-16);
                margin-top: var(--space-16);
                border-top: 1px solid var(--color-invert-component-tr-70);
                column-gap: var(--space-16);
                align-items: center;
                justify-content: flex-end;
            }

            .card-title {
                color: var(--color-invert-component-tr-20);
            }

            .card-container {
                display: flex;
                flex-direction: column;
                padding: var(--space-40) var(--space-48) var(--space-24) var(--space-48);
                width: 100%;
                row-gap: var(--space-24);
            }

            .card-header-container {
                display: flex;
                width: 100%;
                justify-content: space-between;
                align-items: center;
            }

            .badge {
                --background-color: var(--color-invert-component-tr-80);
                --text-color: var(--color-invert-component-tr-20);
                --badge-icon-color: var(--color-invert-component-tr-40);
                box-shadow: unset;
                border: 1px solid var(--badge-border-color);
            }

            .card-badges-container {
                display: flex;
                column-gap: var(--space-16);
                flex-wrap: wrap;
            }

            .card-content-container {
                display: flex;
                flex-direction: column;
                width: 830px;
            }

            .dates-info {
                display: flex;
                flex-direction: column;
                row-gap: var(--space-4);
                padding: var(--space-8) 0;
            }

            .dates-values {
                display: flex;
                gap: var(--space-4);
            }
            
            .dates-label {
                color: var(--color-invert-component-tr-40);
            }

            @media (max-width: 1024px) {
                .short-card-content-container {
                    display: flex;
                    column-gap: 0; 
                }

                .card-footer {
                    margin-top: var(--space-16);
                }
            }

            @media (max-width: 768px) {
                .short-card-container {
                    padding: var(--space-32) var(--space-48) var(--space-16) var(--space-48);
                }

                .short-card-content-container {
                    flex-direction: column;
                    row-gap: var(--space-16);
                    padding-bottom: var(--space-16);
                }

                .short-card-badges {
                    display: flex;
                    column-gap: var(--space-16);
                }

                .short-card-left-part {
                    row-gap: var(--space-16);
                }
            }

            @media (max-width: 600px) {
                :host {
                    border-radius: var(--border-radius-36);
                }

                .short-card-container {
                    padding: var(--space-24);
                }
                
                .short-card-left-part {
                    row-gap: var(--space-24);
                }
                
                .short-card-content-container {
                    padding-bottom: var(--space-24);
                }

                .card-footer {
                    margin-top: 0;
                    column-gap: unset;
                    justify-content: space-between;
                }

                .card-container {
                    padding: var(--space-24);
                }

                .card-badges-container {
                    flex-wrap: wrap;
                    gap: var(--space-8);
                }
            }
        `;
    }

    render() {
        // language=html
        return html` ${this.shortVersion ? this._shortVersionCard : this._card} `;
    }

    static get properties() {
        return {
            shortVersion: {
                type: Boolean,
                attribute: 'short-version',
                reflect: true,
            },
            hasFooter: {
                type: Boolean,
                attribute: 'has-footer',
                reflect: true,
            },
            title: {
                type: String,
            },
            description: {
                type: String,
            },
            giftText: {
                type: String,
                attribute: 'gift-text',
            },
            durationText: {
                type: String,
                attribute: 'duration-text',
            },
            durationIcon: {
                type: String,
                attribute: 'duration-icon',
            },
            locationText: {
                type: String,
                attribute: 'location-text',
            },
            locationIcon: {
                type: String,
                attribute: 'location-icon',
            },
            deviceText: {
                type: String,
                attribute: 'device-text',
            },
            deviceIcon: {
                type: String,
                attribute: 'device-icon',
            },
            datesLabel: {
                type: String,
                attribute: 'dates-label',
            },
            startDate: {
                type: Date,
                attribute: 'start-date',
            },
            endDate: {
                type: String,
                attribute: 'end-date',
            },
        };
    }

    constructor() {
        super();
        this.shortVersion = false;
        this.hasFooter = false;
        this.title = '';
        this.description = '';
        this.giftText = '';
        this.durationText = '';
        this.durationIcon = '';
        this.locationText = '';
        this.locationIcon = '';
        this.deviceText = '';
        this.deviceIcon = '';
        this.datesLabel = '';
        this.startDate = null;
        this.endDate = null;
        this._isLaptop = window.matchMedia(`(max-width: 1024px)`).matches;
        this._isTablet = window.matchMedia(`(max-width: 768px)`).matches;
        this._isMobile = window.matchMedia(`(max-width: 600px)`).matches;
    }

    get _shortVersionCard() {
        return html`
            <div class="short-card-container">
                <div class="short-card-content-container">
                    <div class="short-card-left-part">
                        ${this.title ? html` <bd-font class="card-title" type="title">${this.title}</bd-font> ` : ''}
                        ${this._isTablet ?
                            html`
                                  <div class="short-card-badges">
                                      ${this.giftText ?
                                          html`
                                                <bd-badge-overline class="gift-badge">
                                                    <bd-icon icon="bd:loyalty"> </bd-icon>
                                                    <bd-font type="overline2-ultrabold">
                                                        <span>${this.giftText}</span>
                                                    </bd-font>
                                                </bd-badge-overline>
                                            ` :
                                          ''}
                                      ${this.durationText ?
                                          html` <bd-badge-overline class="badge">
                                                ${this.durationIcon ?
                                                    html` <bd-icon icon="${this.durationIcon}"> </bd-icon> ` :
                                                    ''}
                                                <bd-font type="small-text">
                                                    <span>${this.durationText}</span>
                                                </bd-font>
                                            </bd-badge-overline>` :
                                          ''}
                                  </div>
                              ` :
                            ''}
                        ${this.description ?
                            html` <bd-font class="card-title" type="body2">${this.description}</bd-font> ` :
                            ''}
                    </div>
                    ${!this._isTablet ?
                        html`<div class="short-card-right-part">
                              ${this.giftText ?
                                  html`
                                        <bd-badge-overline class="gift-badge">
                                            <bd-icon icon="bd:loyalty"> </bd-icon>
                                            <bd-font type="overline2-ultrabold">
                                                <span>${this.giftText}</span>
                                            </bd-font>
                                        </bd-badge-overline>
                                    ` :
                                  ''}
                              ${this.durationText ?
                                  html`
                                        <bd-font class="short-card-duration" type="small-text">
                                            ${this.durationText}
                                        </bd-font>
                                    ` :
                                  ''}
                          </div>` :
                        ''}
                </div>
                ${this.hasFooter ?
                    html`
                          <div class="card-footer">
                              <slot name="more-button"></slot>
                              <slot name="register-button"></slot>
                          </div>
                      ` :
                    ''}
            </div>
        `;
    }

    get _card() {
        return html`
            <div class="card-container">
                <div class="card-header-container">
                    ${this.title ? html` <bd-font class="card-title" fixed type="title">${this.title}</bd-font> ` : ''}
                    ${!this._isMobile && this.giftText ?
                        html`
                              <bd-badge-overline class="gift-badge">
                                  <bd-icon icon="bd:loyalty"> </bd-icon>
                                  <bd-font type="overline2-ultrabold">
                                      <span>${this.giftText}</span>
                                  </bd-font>
                              </bd-badge-overline>
                          ` :
                        ''}
                </div>
                <div class="card-badges-container">
                    ${this._isMobile && this.giftText ?
                        html`
                              <bd-badge-overline class="gift-badge">
                                  <bd-icon icon="bd:loyalty"> </bd-icon>
                                  <bd-font type="overline2-ultrabold">
                                      <span>${this.giftText}</span>
                                  </bd-font>
                              </bd-badge-overline>
                          ` :
                        ''}
                    ${this.durationText ?
                        html` <bd-badge-overline class="badge">
                              ${this.durationIcon ? html` <bd-icon icon="${this.durationIcon}"> </bd-icon> ` : ''}
                              <bd-font type="small-text">
                                  <span>${this.durationText}</span>
                              </bd-font>
                          </bd-badge-overline>` :
                        ''}
                    ${this.locationText ?
                        html` <bd-badge-overline class="badge">
                              ${this.locationIcon ? html` <bd-icon icon="${this.locationIcon}"> </bd-icon> ` : ''}
                              <bd-font type="small-text">
                                  <span>${this.locationText}</span>
                              </bd-font>
                          </bd-badge-overline>` :
                        ''}
                    ${this.deviceText ?
                        html` <bd-badge-overline class="badge">
                              ${this.deviceIcon ? html` <bd-icon icon="${this.deviceIcon}"> </bd-icon> ` : ''}
                              <bd-font type="small-text">
                                  <span>${this.deviceText}</span>
                              </bd-font>
                          </bd-badge-overline>` :
                        ''}
                </div>
                ${this.description ?
                    html` <bd-font class="card-title" type="body2">${this.description}</bd-font> ` :
                    ''}
                ${this.datesLabel && this.startDate ?
                    html`
                          <div class="dates-info">
                              ${this.datesLabel ?
                                  html` <bd-font class="dates-label" type="small-text">${this.datesLabel}</bd-font> ` :
                                  ''}
                              ${this.startDate ?
                                  html`
                                        <bd-font class="dates-values" type="number">
                                            <bd-date value=${this.startDate}></bd-date>
                                            ${this.endDate ? html`
                                                - <bd-date value=${this.endDate}></bd-date>
                                            ` : ''}
                                        </bd-font>
                                    ` :
                                  ''}
                          </div>
                      ` :
                    ''}
                ${this.hasFooter ?
                    html`
                          <div class="card-footer">
                              <slot name="more-button"></slot>
                              <slot name="register-button"></slot>
                          </div>
                      ` :
                    ''}
            </div>
        `;
    }
}

window.customElements.define(ResearchCard.is, ResearchCard);
